// First-touch marketing attribution.
//
// Captured once, on whichever page the visitor first lands on, and kept in a 30-day
// cookie. First touch means first touch: if a cookie already exists we leave it alone,
// so a visitor who returns through a different campaign is still credited to the
// channel that originally acquired them.
//
// A cookie (not localStorage) is used deliberately: it's scoped to the registrable
// domain, so a first touch on the marketing site carries over to the app subdomain.

import { countryFromTimezone } from './timezoneCountry';

const COOKIE_NAME = 'joylo_attribution';
const COOKIE_MAX_AGE_DAYS = 30;

// Browser-side IP geolocation; free, no key, CORS-enabled
const IP_COUNTRY_URL = 'https://api.country.is/';

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  // Meta ad-click ID, auto-appended to every Meta ad URL; needed for Conversions API matching
  fbclid?: string;
  referrer?: string;
  landing_path?: string;
  first_touch_at?: string;
  // Browser timezone country — the server prefers a CDN header or IP lookup when it
  // has one, and falls back to this
  tz_country?: string;
  // Country resolved from the visitor's real IP by the browser, which (unlike the
  // server behind an L4 LB) can actually see it. Preferred over tz_country.
  ip_country?: string;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

// Matches the server's column widths — a longer value is truncated rather than dropped
const MAX_FIELD_LEN = 255;
const MAX_URL_LEN = 2000;
const MAX_FBCLID_LEN = 500;

// Cookie is shared across app + marketing subdomains, so scope it to the registrable
// domain. Falls back to a host-only cookie on localhost and anything unrecognized.
const cookieDomain = (): string => {
  const host = window.location.hostname;
  const known = ['joylo.ai', 'joylo.org', 'joylo.io', 'joylo.dev'];
  const match = known.find(d => host === d || host.endsWith(`.${d}`));
  return match ? `; domain=.${match}` : '';
};

const readCookie = (name: string): string | null => {
  try {
    const prefix = `${name}=`;
    const found = document.cookie.split('; ').find(c => c.startsWith(prefix));
    return found ? decodeURIComponent(found.slice(prefix.length)) : null;
  } catch {
    return null;
  }
};

const writeCookie = (name: string, value: string): void => {
  try {
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}${cookieDomain()}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked — attribution is best-effort and must never break the page
  }
};

const truncate = (value: string | null, max: number): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
};

// Our own domains and their subdomains. A marketing-site -> app hop is an internal
// navigation, so matching must cover subdomains, not just the exact current host.
const isSelfHost = (host: string): boolean => {
  const known = ['joylo.ai', 'joylo.org', 'joylo.io', 'joylo.dev'];
  if (host === window.location.hostname) return true;
  return known.some(d => host === d || host.endsWith(`.${d}`));
};

// An external referrer only. A same-site referrer is an internal navigation, not a
// channel, and would otherwise mask the real source on a second-page signup.
export const externalReferrer = (): string | undefined => {
  const ref = document.referrer;
  if (!ref) return undefined;
  try {
    if (isSelfHost(new URL(ref).hostname)) return undefined;
  } catch {
    return undefined;
  }
  return truncate(ref, MAX_URL_LEN);
};

// Read the stored first touch, if any.
export const getStoredAttribution = (): Attribution | null => {
  if (typeof window === 'undefined') return null;
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Attribution;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

// Capture attribution on landing. Safe to call on every route change: it writes at
// most once per 30-day cookie window, and never overwrites an existing first touch.
export const captureAttributionFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  if (getStoredAttribution()) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {};
  for (const key of UTM_KEYS) {
    const value = truncate(params.get(key), MAX_FIELD_LEN);
    if (value) attribution[key] = value;
  }
  const fbclid = truncate(params.get('fbclid'), MAX_FBCLID_LEN);
  if (fbclid) attribution.fbclid = fbclid;

  const referrer = externalReferrer();
  if (referrer) attribution.referrer = referrer;

  // Nothing to attribute and no external referrer: a true direct visit. Still stored,
  // so a later arrival via a campaign link can't retroactively claim this visitor.
  attribution.landing_path = truncate(
    window.location.pathname + window.location.search, MAX_URL_LEN,
  );
  attribution.first_touch_at = new Date().toISOString();

  const tzCountry = countryFromTimezone();
  if (tzCountry) attribution.tz_country = tzCountry;

  writeCookie(COOKIE_NAME, JSON.stringify(attribution));

  // The server sits behind an L4 load balancer, so it only ever sees the ingress pod's
  // IP and cannot geolocate the visitor. The browser can: this resolves the country from
  // the real client IP and rewrites the cookie once it returns. Fire-and-forget — a
  // failure just leaves the timezone value (or nothing) in place.
  resolveCountryFromIp();
};

// Look up the visitor's country by IP and merge it into the stored first touch. Kept
// separate from capture so a slow or blocked request never delays the cookie write.
const resolveCountryFromIp = (): void => {
  try {
    fetch(IP_COUNTRY_URL, { mode: 'cors', credentials: 'omit' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        const code = (data && data.country ? String(data.country) : '').trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(code)) return;
        const stored = getStoredAttribution();
        // Only fill a gap — never overwrite a country already resolved for this first touch
        if (!stored || stored.ip_country) return;
        stored.ip_country = code;
        writeCookie(COOKIE_NAME, JSON.stringify(stored));
      })
      .catch(() => { /* best effort — country is a reporting nicety, not a signup gate */ });
  } catch {
    // ignore
  }
};

// Payload for the signup request. Returns undefined when there's nothing stored, so
// the field is simply omitted rather than sent as null.
export const getAttributionPayload = (): Attribution | undefined => {
  return getStoredAttribution() ?? undefined;
};

// Resolve country right before signup when the landing-time lookup has not filled it
// in yet — a visitor who lands and signs up immediately would otherwise miss it.
// Bounded so a slow or blocked lookup can never hold up the signup request.
export const ensureCountry = async (timeoutMs = 1500): Promise<void> => {
  const stored = getStoredAttribution();
  if (!stored || stored.ip_country) return;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(IP_COUNTRY_URL, { mode: 'cors', credentials: 'omit', signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return;
    const data = await res.json();
    const code = (data && data.country ? String(data.country) : '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return;
    const latest = getStoredAttribution();
    if (!latest || latest.ip_country) return;
    latest.ip_country = code;
    writeCookie(COOKIE_NAME, JSON.stringify(latest));
  } catch {
    // best effort — never block signup on geolocation
  }
};
